import type { SolarProposalSavingsSectionDto } from "~/api/dto/solar-proposal-savings.dto"
import type { SolarProposalStatus } from "~/api/dto/solar-proposal.dto"

import type { SolarHeadline, SolarHeroSavings } from "./solar-presentation"
import type { SolarPresentationHeader } from "./solar-presentation-data"
import { shownComputeBase } from "./solar-proposal-savings"
import {
  formatPersonPhone,
  personName,
} from "../BatteryOnlyProposal/person-display"

/** Statuses a homeowner can be shown. Design failures and rejections are the
 * seller's to resolve, so they map to no label and the eyebrow is hidden. */
const SOLAR_STATUS_LABELS: Partial<Record<SolarProposalStatus, string>> = {
  proposal_created: `Proposal created`,
  design_queued: `Design in progress`,
  design_underway: `Design in progress`,
  design_complete: `Design complete`,
  proposal_published: `Ready to review`,
  summary_confirmed: `Summary confirmed`,
  contract_requested: `Contract requested`,
  contract_sent: `Contract sent`,
  contract_signed: `Contract signed`,
}

/** Statuses that mean a contract exists, so Contract status has something to show. */
const SOLAR_CONTRACT_STATUSES = new Set<SolarProposalStatus>([
  `contract_requested`,
  `contract_sent`,
  `contract_signed`,
])

export const solarHasContract = (status: SolarProposalStatus | null): boolean =>
  status != null && SOLAR_CONTRACT_STATUSES.has(status)

export const solarStatusLabel = (
  status: SolarProposalStatus | null,
): string | null => (status ? (SOLAR_STATUS_LABELS[status] ?? null) : null)

export const toSolarHeadline = (
  proposal: SolarPresentationHeader,
): SolarHeadline => ({
  proposalNumber: proposal.proposalNumber,
  statusLabel: solarStatusLabel(proposal.status),
  createdAt: proposal.createdAt,
  homeowner: {
    name: proposal.homeowner
      ? personName(proposal.homeowner, `Homeowner`)
      : `Homeowner`,
    email: proposal.homeowner?.email || null,
    phone: formatPersonPhone(proposal.homeowner?.phone ?? null),
  },
  consultant: {
    name: personName(proposal.consultant, `Your consultant`),
    email: proposal.consultant.email || null,
    phone: formatPersonPhone(proposal.consultant.phone),
  },
  address: proposal.address,
  utilityName: proposal.utilityName,
  offsetPct: proposal.offsetPct,
  offsetIsAurora: proposal.offsetSource === `aurora`,
  offsetIncludesAiCompute: proposal.includesAiComputeLoad,
})

/**
 * The hero's figure is the savings section's: net savings after the system's
 * cost when the server has them, else utility-bill savings, so the two never
 * disagree. With a compute node it is net savings with the node's guaranteed
 * Rev 2.10 base, never the bonus. A net loss falls back a step (compute to net, net
 * to utility-bill savings) rather than "will save −$X"; the section shows it.
 */
export const toSolarHeroSavings = (
  proposal: Pick<SolarPresentationHeader, `homeowner`>,
  { savings, net }: SolarProposalSavingsSectionDto,
  /** The compute section's base; null without one. */
  ledgerMonthlyBase: number | null = null,
): SolarHeroSavings | null => {
  if (!savings) return null
  const familyName = proposal.homeowner?.lastName.trim() || null
  const compute = shownComputeBase(savings, net, ledgerMonthlyBase)
  if (compute && compute.netSavingsWithCompute > 0) {
    return {
      familyName,
      lifetimeSavings: compute.netSavingsWithCompute,
      termYears: savings.termYears,
      kind: `compute`,
      breakEvenYear: compute.breakEvenYearWithCompute,
    }
  }

  return net && net.netSavings > 0
    ? {
        familyName,
        lifetimeSavings: net.netSavings,
        termYears: savings.termYears,
        kind: `net`,
        breakEvenYear: net.breakEvenYear,
      }
    : {
        familyName,
        lifetimeSavings: savings.totals.savings,
        termYears: savings.termYears,
        kind: `utility`,
        breakEvenYear: null,
      }
}
